import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Stats, Decision, RawEvent, CoverageDocument, ActivityItem, Alert, FilterState, WorkflowStatus, Tab } from '@/types';
import { api } from '@/services/api';

interface AppData {
  // Data
  stats: Stats | null;
  decisions: Decision[];
  rawEvents: RawEvent[];
  coverageDocs: CoverageDocument[];
  activity: ActivityItem[];
  alerts: Alert[];
  // Thresholds
  actionThreshold: number;
  relevanceThreshold: number;
  // Meta
  lastUpdated: Date | null;
  // Actions
  loadContent: () => Promise<void>;
  loadActivity: () => Promise<void>;
  // Tenant
  tenant: string;
  setTenantAndReload: (tenant: string) => void;
  // Tab
  currentTab: Tab;
  setCurrentTab: (tab: Tab) => void;
  // Filters
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  filteredDecisions: Decision[];
  toggleTagFilter: (tag: string) => void;
  clearFilters: () => void;
  // Coverage
  showExpiredCoverage: boolean;
  setShowExpiredCoverage: (v: boolean) => void;
  handleCoverageUpdate: (id: string, content: string) => Promise<void>;
  handleStatusChange: (id: string, status: WorkflowStatus) => Promise<void>;
  handleRegenerate: (id: string, guidance?: string) => Promise<void>;
  // Decision override
  updateDecision: (updated: Decision) => void;
}

const defaultFilters: FilterState = {
  search: '',
  scoreFilter: 'all',
  severityFilter: 'all',
  actionFilter: 'all',
  coveredOnly: false,
  includeExpired: false,
  activeTagFilters: new Set(),
};

export function useAppData(): AppData {
  const location = useLocation();
  const [stats, setStats] = useState<Stats | null>(null);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [rawEvents, setRawEvents] = useState<RawEvent[]>([]);
  const [coverageDocs, setCoverageDocs] = useState<CoverageDocument[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [tenant, setTenant] = useState(() => localStorage.getItem('selectedTenant') || 'klm');
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [actionThreshold, setActionThreshold] = useState(0.75);
  const [relevanceThreshold, setRelevanceThreshold] = useState(0.30);
  const [showExpiredCoverage, setShowExpiredCoverage] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Derive current tab from URL
  const currentTab: Tab = useMemo(() => {
    const p = location.pathname;
    if (p === '/events/raw') return 'raw';
    if (p === '/events/all') return 'all';
    if (p === '/events' || p.startsWith('/events')) return 'relevant';
    if (p === '/live') return 'live';
    if (p === '/coverage') return 'cover';
    if (p === '/alerts') return 'alerts';
    if (p === '/analytics') return 'analytics';
    return 'relevant';
  }, [location.pathname]);

  // No-op setter for backward compat — tab is now URL-driven
  const setCurrentTab = useCallback((_tab: Tab) => {}, []);

  const loadPipelineData = useCallback(async () => {
    try {
      const statsData = await api.getStats();
      setStats(statsData);
      setLastUpdated(new Date());
      if (statsData.action_threshold) setActionThreshold(statsData.action_threshold);
      if (statsData.relevance_threshold) setRelevanceThreshold(statsData.relevance_threshold);
    } catch (err) {
      console.error('Failed to load pipeline data:', err);
    }
  }, []);

  const loadContent = useCallback(async () => {
    try {
      if (currentTab === 'raw') {
        const events = await api.getEvents(filters.includeExpired);
        setRawEvents(events || []);
      } else if (currentTab === 'cover') {
        const docs = await api.getCoverageDocuments(showExpiredCoverage);
        setCoverageDocs(docs || []);
      } else if (currentTab === 'alerts') {
        const data = await api.getAlerts();
        setAlerts(data || []);
      } else {
        const relevant = currentTab === 'relevant';
        const data = await api.getDecisions(relevant ? true : undefined, 100, filters.includeExpired);
        setDecisions(data || []);
      }
    } catch (err) {
      console.error('Failed to load content:', err);
    }
  }, [currentTab, filters.includeExpired, showExpiredCoverage]);

  const loadActivity = useCallback(async () => {
    try {
      const [actData, alertData] = await Promise.all([
        api.getActivity(10),
        api.getAlerts(),
      ]);
      setActivity(actData || []);
      setAlerts(alertData || []);
    } catch (err) {
      console.error('Failed to load activity:', err);
    }
  }, []);

  // Initial load + tenant change
  useEffect(() => {
    api.setTenant(tenant);
    loadPipelineData();
    loadContent();
    loadActivity();
  }, [tenant, loadPipelineData, loadContent, loadActivity]);

  // Polling
  useEffect(() => {
    const statsInterval = setInterval(loadPipelineData, 5000);
    const activityInterval = setInterval(loadActivity, 5000);
    const contentInterval = setInterval(() => {
      if (currentTab !== 'cover') loadContent();
    }, 30000);
    return () => {
      clearInterval(statsInterval);
      clearInterval(activityInterval);
      clearInterval(contentInterval);
    };
  }, [loadPipelineData, loadContent, loadActivity, currentTab]);

  const setTenantAndReload = useCallback((newTenant: string) => {
    setTenant(newTenant);
    api.setTenant(newTenant);
    localStorage.setItem('selectedTenant', newTenant);
    setStats(null);
    setDecisions([]);
    setRawEvents([]);
    setCoverageDocs([]);
    setActivity([]);
    setAlerts([]);
  }, []);

  // Filter decisions
  const filteredDecisions = decisions.filter(d => {
    const { search, scoreFilter, severityFilter, actionFilter, coveredOnly, activeTagFilters } = filters;
    const score = Math.round(d.relevance_score * 100);
    if (search) {
      const searchable = `${d.event_title} ${d.reasoning} ${(d.matched_criteria || []).join(' ')} ${d.event_source}`.toLowerCase();
      if (!searchable.includes(search.toLowerCase())) return false;
    }
    if (scoreFilter === '90' && score < 90) return false;
    if (scoreFilter === '75' && score < 75) return false;
    if (scoreFilter === '50' && score < 50) return false;
    if (scoreFilter === 'below50' && score >= 50) return false;
    if (severityFilter !== 'all' && d.severity !== severityFilter) return false;
    if (actionFilter !== 'all' && (d.action_type || 'monitor') !== actionFilter) return false;
    if (coveredOnly && score < actionThreshold * 100) return false;
    if (activeTagFilters.size > 0) {
      const eventTags = new Set(d.matched_criteria || []);
      if (![...activeTagFilters].every(tag => eventTags.has(tag))) return false;
    }
    return true;
  });

  const toggleTagFilter = (tag: string) => {
    setFilters(prev => {
      const newTags = new Set(prev.activeTagFilters);
      if (newTags.has(tag)) newTags.delete(tag); else newTags.add(tag);
      return { ...prev, activeTagFilters: newTags };
    });
  };

  const clearFilters = () => setFilters(defaultFilters);

  const handleCoverageUpdate = async (id: string, content: string) => {
    await api.updateCoverageDocument(id, content);
    const docs = await api.getCoverageDocuments();
    setCoverageDocs(docs || []);
  };

  const handleStatusChange = async (id: string, status: WorkflowStatus) => {
    await api.updateWorkflowStatus(id, status);
    const docs = await api.getCoverageDocuments();
    setCoverageDocs(docs || []);
  };

  const handleRegenerate = async (id: string, guidance?: string) => {
    await api.regenerateDocument(id, guidance);
    const docs = await api.getCoverageDocuments();
    setCoverageDocs(docs || []);
  };

  /** Patch a single decision in the local state (e.g. after override) and re-sort. */
  const updateDecision = useCallback((updated: Decision) => {
    setDecisions(prev => {
      const next = prev.map(d => d.id === updated.id ? updated : d);
      // Re-sort by effective score descending so overridden scores reorder immediately
      next.sort((a, b) => {
        const scoreA = a.override_score ?? a.relevance_score;
        const scoreB = b.override_score ?? b.relevance_score;
        return scoreB - scoreA;
      });
      return next;
    });
  }, []);

  return {
    stats, decisions, rawEvents, coverageDocs, activity, alerts,
    actionThreshold, relevanceThreshold, lastUpdated,
    loadContent, loadActivity,
    tenant, setTenantAndReload,
    currentTab, setCurrentTab,
    filters, setFilters, filteredDecisions, toggleTagFilter, clearFilters,
    showExpiredCoverage, setShowExpiredCoverage,
    handleCoverageUpdate, handleStatusChange, handleRegenerate,
    updateDecision,
  };
}
